import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "react-toastify";
import { errorLogger } from "@/utils/helper";
import {
  useCreateMerchant,
  useGetAllBanks,
  useGetSettlementCycle,
} from "./mutations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  merchantName: z.string().min(1, "Merchant Name is required"),
  email: z.string().email("Invalid email address"),
  address: z.string().min(1, "Address is required"),
  routeCode: z.string().min(1, "Route Code is required"),
  merchantCode: z.string().min(1, "Merchant Code is required").optional(),
  routeName: z.string().min(1, "Route Name is required"),
  mobilePhone: z.string().min(10, "Invalid phone number"),
  state: z.string().min(1, "State is required"),
  lga: z.string().min(1, "LGA is required"),
  accountNumber: z.string().min(10, "Invalid account number"),
  bankCode: z.string().min(3, "Bank Code is required"),
  merchantType: z.string().min(1, "Merchant Type is required"),
  settlementCycle: z.string().optional(),
  bankName: z.string().optional(),
  bvn: z.union([
    z.string().length(0),
    z.string().length(11, "BVN must be 11 characters")
  ]).optional(),
});

export type MerchantDTO = z.infer<typeof formSchema>;

type MerchantFormProps = {
  onSubmit: (data: MerchantDTO) => void;
  onClose: () => void;
  isPending: boolean;
};

function MerchantForm({ onSubmit, onClose, isPending }: MerchantFormProps) {
  const form = useForm<MerchantDTO>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      merchantName: "",
      email: "",
      address: "",
      routeCode: "",
      merchantCode: "0000",
      routeName: "",
      mobilePhone: "",
      state: "",
      lga: "",
      accountNumber: "",
      bankCode: "",
      bvn: "",
    },
  });

  const { data: banks } = useGetAllBanks();
  const { data: settlementCycle, } =
    useGetSettlementCycle();


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="merchantName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant Name</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="merchantType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant Name</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a merchant type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DirectlyAcquired">
                      Bestaf Merchant
                    </SelectItem>
                    <SelectItem value="BankAcquired">
                      Bank-Acquired Merchant
                    </SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="routeCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Route Code</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* <FormField
          control={form.control}
          name="merchantCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Merchant Code</FormLabel>
              <FormControl>
                <Input disabled={true} {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        /> */}
        <FormField
          control={form.control}
          name="routeName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Route Name</FormLabel>
              <FormControl>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select a route" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nibss">Nibss</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="mobilePhone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mobile Phone </FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormLabel>State</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lga"
          render={({ field }) => (
            <FormItem>
              <FormLabel>LGA</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {form.watch("merchantType") === "DirectlyAcquired" && (
          <FormField
            control={form.control}
            name="accountNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Account Number</FormLabel>
                <FormControl>
                  <Input {...field} className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="bankCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bank Code</FormLabel>
              <FormControl>
                <Input {...field} className="h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch("merchantType") === "BankAcquired" && (
          <>
            <FormField
              control={form.control}
              name="bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={`${field.value}`}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full px-4">
                          <SelectValue placeholder="Select Merchant" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {banks?.map((v: any, key: number) => (
                          <SelectItem key={key} value={v.bankName}>
                            {v.bankName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Number</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bvn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>BVN</FormLabel>
                  <FormControl>
                    <Input {...field} className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="settlementCycle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Settlement Cycle</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select a settlement cycle" />
                      </SelectTrigger>
                      <SelectContent>
                        {settlementCycle &&
                          Object.entries(settlementCycle || {}).map(
                            (v: any, key: number) => {
                              return (
                                <SelectItem value={v[0]} key={key}>
                                  {v[1]}
                                </SelectItem>
                              );
                            }
                          )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <DialogFooter className="flex md:flex-row flex-col-reverse justify-end gap-4">
          <Button variant="outline" onClick={onClose} className="border-primary">
            Cancel
          </Button>
          <Button disabled={isPending} isLoading={isPending} type="submit" className="bg-primary">
            Submit
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

type MerchantFormModalProps = {
  isOpen?: boolean;
  onClose: () => void;
};

function MerchantFormModal({ isOpen, onClose }: MerchantFormModalProps) {
  const { mutateAsync, isPending } = useCreateMerchant();

  async function handleSubmit(data: MerchantDTO) {
    try {
      // Remove empty BVN from the data and convert email to lowercase
      const cleanedData = {
        ...data,
        email: data.email.toLowerCase(),
        bvn: data.bvn && data.bvn.trim() !== "" ? data.bvn : undefined
      };

      await mutateAsync(cleanedData, {
        onSuccess() {
          toast.success("Merchant Created Successfully");
          onClose();
        },
      });
    } catch (error) {
      errorLogger(error);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-[75%] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Merchant</DialogTitle>
        </DialogHeader>
        <MerchantForm
          onSubmit={handleSubmit}
          onClose={onClose}
          isPending={isPending}
        />
      </DialogContent>
    </Dialog>
  );
}

export default MerchantFormModal;
